import React from 'react';
import { Link } from 'react-router-dom';
import { Translation } from '../translations';
import { useToast } from '../context/ToastContext';

interface FooterProps {
  t: Translation;
}

const Footer: React.FC<FooterProps> = ({ t }) => {
  const { showToast } = useToast();
  
  const handleComingSoon = (e: React.MouseEvent) => {
    e.preventDefault();
    const lang = t.footer.copyright.includes('Groove') ? 'en' : 'ko';
    showToast(lang === 'ko' ? '🔨 준비 중입니다' : '🔨 Coming Soon');
  };
  // Helper function to get legal page links
  const getLegalLink = (linkText: string) => {
    const lowerText = linkText.toLowerCase();
    if (lowerText.includes('privacy') || lowerText.includes('개인정보')) {
      return '/privacy';
    }
    if (lowerText.includes('terms') || lowerText.includes('약관') || lowerText.includes('이용')) {
      return '/terms';
    }
    if (lowerText.includes('refund') || lowerText.includes('환불')) {
      return '/refund';
    }
    if (lowerText.includes('cookie') || lowerText.includes('쿠키')) {
      return '/cookies';
    }
    return '#legal';
  };

  // Helper function to get product links
  const getProductLink = (linkText: string) => {
    const lowerText = linkText.toLowerCase();
    if (lowerText.includes('pricing') || lowerText.includes('요금제')) {
      return '/#pricing';
    }
    if (lowerText.includes('features') || lowerText.includes('기능')) {
      return '/#features';
    }
    if (lowerText.includes('download') || lowerText.includes('다운로드')) {
      return '/download';
    }
    return '#product';
  };
  return (
    <footer className="bg-gray-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Logo and Description */}
          <div className="md:col-span-1">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <span className="text-primary-600 text-xl font-bold">A</span>
              </div>
              <span className="text-xl font-bold">AivueBiz</span>
            </div>
            <p className="text-gray-400 text-sm">
              {t.footer.madeWith}
            </p>
          </div>
          
          {/* Footer Links */}
          <div>
            <h4 className="font-semibold mb-4">{t.footer.company.title}</h4>
            <ul className="space-y-2">
              {t.footer.company.links.map((link, index) => {
                const lowerLink = link.toLowerCase();
                if (lowerLink.includes('about') || lowerLink.includes('소개')) {
                  return (
                    <li key={index}>
                      <Link to="/about" className="text-gray-400 hover:text-white transition text-sm">
                        {link}
                      </Link>
                    </li>
                  );
                }
                return (
                  <li key={index}>
                    <a href="#" onClick={handleComingSoon} className="text-gray-400 hover:text-white transition text-sm cursor-pointer">
                      {link}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">{t.footer.product.title}</h4>
            <ul className="space-y-2">
              {t.footer.product.links.map((link, index) => {
                const linkUrl = getProductLink(link);
                if (linkUrl.startsWith('/#') || linkUrl === '/download') {
                  if (linkUrl === '/download') {
                    return (
                      <li key={index}>
                        <Link to={linkUrl} className="text-gray-400 hover:text-white transition text-sm">
                          {link}
                        </Link>
                      </li>
                    );
                  }
                  return (
                    <li key={index}>
                      <a href={linkUrl} className="text-gray-400 hover:text-white transition text-sm">
                        {link}
                      </a>
                    </li>
                  );
                }
                return (
                  <li key={index}>
                    <a href="#" onClick={handleComingSoon} className="text-gray-400 hover:text-white transition text-sm cursor-pointer">
                      {link}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
          
          {/* Resources section - temporarily commented out
          <div>
            <h4 className="font-semibold mb-4">{t.footer.resources.title}</h4>
            <ul className="space-y-2">
              {t.footer.resources.links.map((link, index) => (
                <li key={index}>
                  <a href="#" onClick={handleComingSoon} className="text-gray-400 hover:text-white transition text-sm cursor-pointer">
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          */}
          
          <div>
            <h4 className="font-semibold mb-4">{t.footer.legal.title}</h4>
            <ul className="space-y-2">
              {t.footer.legal.links.map((link, index) => (
                <li key={index}>
                  <Link to={getLegalLink(link)} className="text-gray-400 hover:text-white transition text-sm">
                    {link}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="mt-12 pt-8 border-t border-gray-800 text-center text-gray-400 text-sm">
          {t.footer.copyright}
        </div>
      </div>
    </footer>
  );
};

export default Footer;